// The places lab's world (gate C4): a small town square, a ruins corner, a stream with a footbridge and a pond, and a
// crypt away from the town, all from the content (src/places/content.ts). Built once, shared by the sim (colliders,
// water volumes, interiors) and the view (meshes):
//   - the ground: flat in the town, gentle bumps beyond, hills at the edge, a channel under the stream and a basin
//     under the pond carved from the water's own outline (one height function for the heightfield collider and the
//     ground mesh);
//   - the water: the stream as an @rpg/collision channel volume (its surface falling along its course), the pond as a
//     polygon volume (a seeded wobbly outline);
//   - the pieces: each placement built by @rpg/meshkit's kit (colliders never depend on the tier, so the sim is the
//     same on a phone and a desktop), standing on the ground;
//   - the interiors: every placed room is a seamless interior, except a placement with `transitionTo`, whose doorway
//     becomes a transition door into the other placement's room (which is then a transition interior), and back.
// The world stream's layout replaces the lab's placements and stream course when the streams merge; the kit, the water
// and the interiors code take any placement list.
import * as THREE from "three";
import { waterChannel, waterLevel, waterPolygon, waterShapeDistance, type BodySpec, type WaterVolume } from "@rpg/collision";
import { createKit, placeInterior, seeded, yawRotation, type KitPiece, type KitPlacement, type KitTier } from "@rpg/meshkit";
import type { InteriorSpec, TransitionDoor } from "@rpg/world";
import type { PlacementRow, PlacesContent, WaterBodyRow } from "./content";

/** The heightfield: ±HALF m in x and z, CELLS cells a side. */
export const GROUND = { half: 64, cells: 128 } as const;
/** Where the Wanderer starts: on the square, south of the well, facing north (yaw π faces −z). */
export const PLACES_SPAWN = { x: 0, z: 6, yaw: Math.PI };
/** The stub mount, out of the way. */
export const PLACES_MOUNT_SPAWN = { x: -40, z: -40, yaw: 0 };

/** A placed piece. */
export interface PlacedPiece {
  id: string;
  row: PlacementRow;
  piece: KitPiece;
  place: KitPlacement;
  group: "town" | "remote";
  /** Its owner number for the cut-away (unique across the batches). */
  owner: number;
}

/** The lab's world. */
export interface PlacesWorld {
  /** The ground's height (no water). */
  ground(x: number, z: number): number;
  /** The heightfield samples (row-major from −z, as @rpg/collision's heightfield). */
  heights: number[];
  water: WaterVolume[];
  placed: PlacedPiece[];
  /** The fixed bodies: the terrain, the slab under the crypt, one per placed piece. */
  bodies: BodySpec[];
  interiors: InteriorSpec[];
  doors: TransitionDoor[];
  /** Interior ID → the placed piece it belongs to. */
  rooms: Map<string, PlacedPiece>;
  /** The slab the remote room stands on (drawn by the view as ground). */
  slabs: { x: number; z: number; hx: number; hz: number; top: number }[];
}

const smooth = (e0: number, e1: number, x: number): number => {
  const t = Math.min(1, Math.max(0, (x - e0) / (e1 - e0)));
  return t * t * (3 - 2 * t);
};

/** A pond's outline: `sides` points round the centre, each radius wobbled by the seed. */
export function pondOutline(r: WaterBodyRow): [number, number][] {
  const [cx, cz] = r.centre as [number, number];
  const rng = seeded(r.seed ?? 1);
  const n = r.sides ?? 12;
  const out: [number, number][] = [];
  for (let i = 0; i < n; i++) {
    const a = (i / n) * Math.PI * 2;
    const rr = r.radius! * (1 + (rng() * 2 - 1) * (r.wobble ?? 0));
    out.push([cx + Math.cos(a) * rr, cz + Math.sin(a) * rr]);
  }
  return out;
}

/** The water volumes from the water rows. */
export function waterVolumes(rows: readonly WaterBodyRow[]): WaterVolume[] {
  return rows.map((r) => {
    if (r.kind === "stream") return waterChannel(r.id, r.points!.map(([x, z, halfWidth, surface]) => ({ x: x!, z: z!, halfWidth: halfWidth!, surface: surface! })), r.depth + 0.4);
    return waterPolygon(r.id, pondOutline(r), r.surface!, r.depth + 0.4);
  });
}

/** Build the lab's world. */
export function buildPlacesWorld(c: PlacesContent, tier: KitTier): PlacesWorld {
  const water = waterVolumes(c.water);
  const depthOf = new Map(c.water.map((r) => [r.id, r.depth]));
  const base = (x: number, z: number): number => {
    const r = Math.hypot(x, z);
    const edge = Math.max(Math.abs(x), Math.abs(z));
    return 0.35 * Math.sin(x * 0.13) * Math.cos(z * 0.11) * smooth(18, 30, r) + 2.2 * smooth(40, 58, edge);
  };
  // the ground: the base, brought to a bank by the water (down, or up into a levee where the ground dips below the
  // water) and into its channel or basin; the lowest of the waters' shapes wins where two meet
  const ground = (x: number, z: number): number => {
    const b = base(x, z);
    let h = Infinity;
    for (const w of water) {
      if (x < w.min.x - 4 || x > w.max.x + 4 || z < w.min.z - 4 || z > w.max.z + 4) continue;
      const d = waterShapeDistance(w, x, z);
      if (d < -3) continue;
      const s = waterLevel(w, x, z);
      // the bank stands just above the water whatever the ground does (a levee where the ground dips)
      const top = s + 0.25;
      const depth = depthOf.get(w.id) ?? 0.5;
      const reach = w.shape?.type === "polygon" ? 3 : 1.1;
      const v = d < -1 ? b + (top - b) * smooth(-3, -1, d) : top + (s - depth - top) * smooth(-0.8, reach, d);
      h = Math.min(h, v);
    }
    return h === Infinity ? b : h;
  };
  const n = GROUND.cells;
  const heights: number[] = [];
  for (let iz = 0; iz <= n; iz++) for (let ix = 0; ix <= n; ix++) heights.push(ground((ix / n - 0.5) * 2 * GROUND.half, (iz / n - 0.5) * 2 * GROUND.half));
  const bodies: BodySpec[] = [
    { id: "terrain", kind: "fixed", colliders: [{ shape: { type: "heightfield", rows: n, cols: n, heights, size: { x: 2 * GROUND.half, y: 1, z: 2 * GROUND.half } }, layer: "terrain", tag: "grass" }] },
  ];

  // the pieces, on the ground (a house's floor above it everywhere; a ruin's foot below it everywhere)
  const kit = createKit({ defs: c.defs, palette: c.palette, material: new THREE.MeshBasicMaterial() });
  // owner numbers run across both batches, so one cut-away owner names one piece wherever it is drawn
  let owner = 0;
  const placed: PlacedPiece[] = c.placements.map((row) => {
    const piece = kit.build(row.piece.id, row.seed, tier);
    const [x, z] = row.at as [number, number];
    const group = row.group ?? "town";
    const bb = new THREE.Box3().setFromBufferAttribute(piece.geometry.getAttribute("position") as THREE.BufferAttribute);
    const cs = Math.cos(row.yaw);
    const sn = Math.sin(row.yaw);
    const under = [
      [0, 0],
      [bb.min.x, bb.min.z],
      [bb.max.x, bb.min.z],
      [bb.min.x, bb.max.z],
      [bb.max.x, bb.max.z],
    ].map(([lx, lz]) => ground(x + lx! * cs + lz! * sn, z - lx! * sn + lz! * cs));
    const house = c.defs[row.piece.id]!.kind === "house";
    const bridge = c.defs[row.piece.id]!.kind === "bridge";
    const y = house ? Math.max(...under) : bridge ? Math.max(under[1]!, under[3]!, under[2]!, under[4]!) - 0.05 : Math.min(...under) - 0.03;
    return { id: row.id, row, piece, place: { x, y, z, yaw: row.yaw }, group, owner: ++owner };
  });
  for (const p of placed)
    if (p.piece.colliders.length)
      bodies.push({ id: `kit:${p.id}`, kind: "fixed", position: { x: p.place.x, y: p.place.y, z: p.place.z }, rotation: yawRotation(p.place.yaw), colliders: p.piece.colliders.map((cd) => ({ ...cd, layer: "solid" })) });

  // a slab of ground under a remote room (it stands outside the heightfield)
  const slabs: PlacesWorld["slabs"] = [];
  for (const p of placed)
    if (p.group === "remote") {
      const s = { x: p.place.x, z: p.place.z, hx: 12, hz: 12, top: p.place.y };
      slabs.push(s);
      bodies.push({ id: `slab:${p.id}`, kind: "fixed", position: { x: s.x, y: s.top - 0.5, z: s.z }, colliders: [{ shape: { type: "cuboid", halfExtents: { x: s.hx, y: 0.5, z: s.hz } }, layer: "terrain", tag: "stone" }] });
    }

  // the interiors and the transition doors
  const interiors: InteriorSpec[] = [];
  const doors: TransitionDoor[] = [];
  const rooms = new Map<string, PlacedPiece>();
  const byId = new Map(placed.map((p) => [p.id, p]));
  const targets = new Set(c.placements.flatMap((r) => (r.transitionTo ? [r.transitionTo.id] : [])));
  const arrive = c.look.interiors.arrive;
  const radius = c.look.interiors.doorRadius;
  for (const p of placed) {
    const i = p.piece.interior;
    if (!i || p.row.transitionTo) continue;
    const w = placeInterior(i, p.place);
    interiors.push({ id: p.id, mode: targets.has(p.id) ? "transition" : "seamless", bounds: w.bounds, profile: i.profile, light: i.light });
    rooms.set(p.id, p);
  }
  for (const s of placed) {
    if (!s.row.transitionTo) continue;
    const t = byId.get(s.row.transitionTo.id);
    if (!s.piece.interior || !t?.piece.interior) throw new Error(`places: transition ${s.id} → ${s.row.transitionTo.id} needs two rooms`);
    const sd = placeInterior(s.piece.interior, s.place).door;
    const td = placeInterior(t.piece.interior, t.place).door;
    const floor = (p: PlacedPiece): number => p.place.y + p.piece.interior!.bounds.min.y + 0.25;
    doors.push({
      id: `${s.id}>${t.id}`,
      at: { x: sd.x - sd.nx * 0.25, y: floor(s), z: sd.z - sd.nz * 0.25 },
      radius,
      to: { x: td.x - td.nx * arrive, y: floor(t), z: td.z - td.nz * arrive, yaw: Math.atan2(-td.nx, -td.nz) },
    });
    const ox = sd.x + sd.nx * arrive;
    const oz = sd.z + sd.nz * arrive;
    doors.push({
      id: `${t.id}>${s.id}`,
      at: { x: td.x - td.nx * 0.25, y: floor(t), z: td.z - td.nz * 0.25 },
      radius,
      to: { x: ox, y: ground(ox, oz) + 0.02, z: oz, yaw: Math.atan2(sd.nx, sd.nz) },
    });
  }
  return { ground, heights, water, placed, bodies, interiors, doors, rooms, slabs };
}
